import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, MenuItem, IconButton, Tooltip, Paper, Chip, FormControl, InputLabel, Select, Stack } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Plus, Search, Eye, Ban, Clock, CheckCircle2, XCircle, AlertCircle, CalendarDays, FilterX, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { tokens } from '@/theme/tokens';
import { PageHeader } from '@/components/navigation/PageHeader';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import useLeaveStore from '@/store/leaveStore';
import { ApplyLeaveModal } from './components/ApplyLeaveModal';
import { LeaveTimelineModal } from './components/LeaveTimelineModal';
import { ErrorState } from '@/components/feedback/ErrorState';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, borderBottom: `1px solid ${tokens.colors.border}`, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export function MyLeavesPage() {
  const { myLeaves, balances, loading, error, fetchMyLeaves, fetchBalances, cancelLeave } = useLeaveStore();

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Confirmation dialog state for cancellation
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [targetLeaveId, setTargetLeaveId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchMyLeaves();
    fetchBalances();
  }, []);

  const handleOpenCancelDialog = (leaveId) => {
    setTargetLeaveId(leaveId);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!targetLeaveId) return;
    setCancelLoading(true);
    try {
      await cancelLeave(targetLeaveId);
      toast.success('Leave request cancelled successfully');
      setCancelDialogOpen(false);
      setTargetLeaveId(null);
      fetchMyLeaves();
      fetchBalances();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel leave request');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleOpenTimeline = (leaveData) => {
    setSelectedLeaveId(leaveData);
    setTimelineModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return format(d, 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'APPROVED':
        return <Chip label="Approved" color="success" size="small" icon={<CheckCircle2 size={14} />} />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" icon={<XCircle size={14} />} />;
      case 'CANCELLED':
        return <Chip label="Cancelled" color="default" size="small" icon={<AlertCircle size={14} />} />;
      case 'PENDING':
      default:
        return <Chip label="Pending" color="warning" size="small" icon={<Clock size={14} />} />;
    }
  };

  const filteredLeaves = useMemo(() => {
    return myLeaves.filter((l) => {
      const matchesStatus = statusFilter === 'ALL' || String(l.status).toUpperCase() === statusFilter;
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        (l.leaveType && l.leaveType.toLowerCase().includes(q)) ||
        (l.reason && l.reason.toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
  }, [myLeaves, statusFilter, searchTerm]);

  // Aggregate stats
  const totalAllocated = balances.reduce((acc, b) => acc + (Number(b.allocated) || 0), 0);
  const totalUsed = balances.reduce((acc, b) => acc + (Number(b.used) || 0), 0);
  const totalRemaining = balances.reduce((acc, b) => acc + (Number(b.remaining) || 0), 0);
  const pendingCount = myLeaves.filter((l) => String(l.status).toUpperCase() === 'PENDING').length;

  const columns = [
    {
      field: 'leaveType',
      headerName: 'Leave Type',
      flex: 1.2,
      minWidth: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value || 'Leave'}
        </Typography>
      ),
    },
    {
      field: 'fromDate',
      headerName: 'From Date',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'toDate',
      headerName: 'To Date',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'numberOfDays',
      headerName: 'Days',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1.5,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const text = params.value || params.row?.reason || '-';
        return (
          <Tooltip title={text} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                cursor: 'pointer',
              }}
            >
              {text}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.3,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const statusUpper = String(params.row.status || '').toUpperCase();
        const canCancel = statusUpper === 'PENDING';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenTimeline(params.row)}>
                <Eye size={18} />
              </IconButton>
            </Tooltip>

            {canCancel && (
              <Tooltip title="Cancel Leave Request">
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Ban size={14} />}
                  onClick={() => handleOpenCancelDialog(params.row.id)}
                  sx={{ px: 1, py: 0.25, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                  Cancel Leave
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const cardColors = ['primary', 'info', 'warning', 'success', 'secondary'];

  if (error) {
    return (
      <Box sx={{ pb: 4 }}>
        <PageHeader
          title="My Leave Dashboard"
          subtitle="View leave balances, apply for leave, and track request status."
        />
        <ErrorState
          error={error}
          onRetry={() => {
            fetchMyLeaves();
            fetchBalances();
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="My Leave Dashboard"
        subtitle="View leave balances, apply for leave, and track request status."
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setApplyModalOpen(true)}
            sx={{ borderRadius: tokens.radius.button }}
          >
            Apply for Leave
          </Button>
        }
      />

      {/* 4 Stat Cards in 1 Row */}
      <Box sx={{ mb: 3, mt: 1 }}>
        <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }}>
          <StatCard
            title="Total Allocated"
            value={`${totalAllocated} Days`}
            icon={CalendarDays}
            color="primary"
          />
          <StatCard
            title="Leaves Used"
            value={`${totalUsed} Days`}
            icon={UserCheck}
            color="warning"
          />
          <StatCard
            title="Remaining Balance"
            value={`${totalRemaining} Days`}
            icon={CheckCircle2}
            color="success"
          />
          <StatCard
            title="Pending Requests"
            value={`${pendingCount} Pending`}
            icon={Clock}
            color="info"
          />
        </DashboardGrid>
      </Box>

      {/* Breakdown per Leave Type if balances exist */}
      {balances.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Leave Type Breakdown ({new Date().getFullYear()})
          </Typography>
          <DashboardGrid columns={{ xs: 1, sm: 2, md: Math.min(balances.length, 4) }}>
            {balances.map((bal, idx) => (
              <StatCard
                key={idx}
                title={`${bal.leaveType} ${bal.code ? `(${bal.code})` : ''}`}
                value={`${bal.remaining} Left`}
                trendValue={`Allocated: ${bal.allocated} | Used: ${bal.used}`}
                icon={CalendarDays}
                color={cardColors[idx % cardColors.length]}
              />
            ))}
          </DashboardGrid>
        </Box>
      )}

      {/* Data Table wrapped in WidgetContainer */}
      <WidgetContainer noPadding>
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by leave type or reason..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>

          {(searchTerm || statusFilter !== 'ALL') && (
            <Button
              variant="outlined"
              startIcon={<FilterX size={18} />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('ALL');
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={filteredLeaves}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id || Math.random().toString()}
            pageSizeOptions={[10, 20, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
            }}
          />
        </Box>
      </WidgetContainer>

      {/* Apply Leave Modal */}
      <ApplyLeaveModal
        open={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        onSuccess={() => {
          fetchMyLeaves();
          fetchBalances();
        }}
      />

      {/* Timeline / Detail Modal */}
      <LeaveTimelineModal
        open={timelineModalOpen}
        onClose={() => setTimelineModalOpen(false)}
        leaveData={selectedLeaveId}
        onCancel={handleOpenCancelDialog}
      />

      {/* Styled Confirmation Dialog for Deleting / Cancelling Leave */}
      <ConfirmationDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Leave Request"
        message="Are you sure you want to cancel this leave request? This action cannot be undone."
        confirmText="Cancel Leave"
        variant="danger"
        loading={cancelLoading}
      />
    </Box>
  );
}
